import { useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Milk, User, Lock, Shield } from "lucide-react";

const LOTA_IMG = "https://static.prod-images.emergentagent.com/jobs/e14b0732-6b72-4e6d-935c-0035d2b6e307/images/296bead7ce024d2338e605450c0c4253ef701f38b4c8025fd33afc2a0c9dd694.png";

export default function Login() {
  const [params] = useSearchParams();
  const [tab, setTab] = useState(params.get("admin") === "1" ? "admin" : "customer");
  const [custForm, setCustForm] = useState({ email: "", password: "" });
  const [adminForm, setAdminForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const { state } = useLocation();

  const doLogin = async (e, forceRole) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const form = forceRole === "admin" ? adminForm : custForm;
    const res = await login(form.email, form.password);
    setLoading(false);
    if (res.ok) {
      if (forceRole === "admin" && res.user.role !== "admin") {
        setError("These credentials are not for an admin account.");
        return;
      }
      const dest = state?.from || (res.user.role === "admin" ? "/admin" : "/dashboard");
      navigate(dest);
    } else {
      setError(res.error);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-dd-page" data-testid="login-page">
      {/* Left: gold lota image */}
      <div className="hidden md:block relative bg-dd-green-deep overflow-hidden">
        <img src={LOTA_IMG} alt="Gold milk pot" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-dd-green-deep/60 via-transparent to-[#0A1F15]/80" />
        <div className="absolute top-10 left-12 flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-dd-gold/15 border border-dd-gold flex items-center justify-center">
            <Milk className="w-5 h-5 text-dd-gold" strokeWidth={1.75} />
          </div>
          <div>
            <div className="font-serif italic text-2xl text-dd-cream">Divya Dhara</div>
            <div className="dd-label text-[0.58rem] text-dd-gold">A2 Gaumata Milk</div>
          </div>
        </div>
        <div className="absolute bottom-12 left-12 right-12 text-dd-cream">
          <div className="dd-label text-dd-gold mb-3 tracking-[0.3em]">DIVYA DHARA</div>
          <h2 className="font-serif text-5xl leading-tight font-devanagari text-dd-gold-soft" data-testid="login-tagline">
            हर बूंद में अमृत
          </h2>
          <p className="mt-4 text-[#E8DEC7] text-lg">Har Boond Mein Amrit · Pure A2 Gir Cow Milk</p>
        </div>
      </div>

      {/* Right: tabs */}
      <div className="flex items-center justify-center p-6 md:p-12 bg-dd-page">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <Link to="/" className="flex items-center gap-2 mb-6 md:hidden">
            <div className="w-9 h-9 rounded-full bg-dd-gold/15 border border-dd-gold flex items-center justify-center">
              <Milk className="w-5 h-5 text-dd-gold" />
            </div>
            <div>
              <div className="font-serif text-xl text-dd-cream">Divya Dhara</div>
              <div className="dd-label text-[0.6rem] text-dd-gold font-devanagari">हर बूंद में अमृत</div>
            </div>
          </Link>

          {/* Tabs */}
          <div className="grid grid-cols-2 gap-0 bg-white/5 border border-dd-gold/30 rounded-sm p-1 mb-7" data-testid="login-tabs">
            <button
              onClick={() => { setTab("customer"); setError(""); }}
              className={`flex items-center justify-center gap-2 py-2.5 rounded-sm text-sm font-semibold transition-all ${
                tab === "customer"
                  ? "bg-gradient-to-b from-dd-gold to-[#8F6E28] text-dd-ink-dark shadow-md"
                  : "text-dd-cream-muted hover:text-dd-cream"
              }`}
              data-testid="tab-customer"
            >
              <User className="w-4 h-4" strokeWidth={2} /> Customer Login
            </button>
            <button
              onClick={() => { setTab("admin"); setError(""); }}
              className={`flex items-center justify-center gap-2 py-2.5 rounded-sm text-sm font-semibold transition-all ${
                tab === "admin"
                  ? "bg-gradient-to-b from-dd-gold to-[#8F6E28] text-dd-ink-dark shadow-md"
                  : "text-dd-cream-muted hover:text-dd-cream"
              }`}
              data-testid="tab-admin"
            >
              <Lock className="w-4 h-4" strokeWidth={2} /> Admin Login
            </button>
          </div>

          {tab === "customer" ? (
            <>
              <div className="dd-label text-dd-gold mb-2">Welcome back</div>
              <h1 className="font-serif text-4xl text-dd-cream">Namaste 🙏</h1>
              <p className="mt-2 text-dd-cream-muted text-sm">Sign in to manage your milk subscription.</p>

              <form onSubmit={(e) => doLogin(e, "customer")} className="mt-7 space-y-4" data-testid="login-form-customer">
                <Field label="Email" type="email" value={custForm.email}
                  onChange={(v) => setCustForm({ ...custForm, email: v })} testid="login-email" />
                <Field label="Password" type="password" value={custForm.password}
                  onChange={(v) => setCustForm({ ...custForm, password: v })} testid="login-password" />
                {error && <div className="text-sm text-[#F5A99A]" data-testid="login-error">{error}</div>}
                <button type="submit" disabled={loading}
                  className="w-full bg-gradient-to-b from-dd-gold to-[#8F6E28] text-dd-ink-dark py-3 rounded-sm font-bold hover:from-dd-gold-light hover:to-dd-gold transition-colors disabled:opacity-60 tracking-wide"
                  data-testid="login-submit">
                  {loading ? "Signing in…" : "Sign In as Customer"}
                </button>
              </form>

              <div className="mt-6 text-sm text-dd-cream-muted">
                New to Divya Dhara?{" "}
                <Link to="/register" className="text-dd-gold font-medium hover:underline" data-testid="login-to-register">
                  Create an account
                </Link>
              </div>
            </>
          ) : (
            <>
              <div className="dd-label text-dd-gold mb-2 flex items-center gap-1.5">
                <Shield className="w-3.5 h-3.5" /> Secure Admin Portal
              </div>
              <h1 className="font-serif text-4xl text-dd-cream">Owner Login</h1>
              <p className="mt-2 text-dd-cream-muted text-sm">Control panel for Matrubhumi Gauras Bhandar operations.</p>

              <form onSubmit={(e) => doLogin(e, "admin")} className="mt-7 space-y-4" data-testid="login-form-admin">
                <Field label="Email ID" type="email" value={adminForm.email}
                  onChange={(v) => setAdminForm({ ...adminForm, email: v })} testid="admin-email" />
                <Field label="Password" type="password" value={adminForm.password}
                  onChange={(v) => setAdminForm({ ...adminForm, password: v })} testid="admin-password" />
                {error && <div className="text-sm text-[#F5A99A]" data-testid="admin-error">{error}</div>}
                <button type="submit" disabled={loading}
                  className="w-full bg-gradient-to-b from-dd-gold to-[#8F6E28] text-dd-ink-dark py-3 rounded-sm font-bold hover:from-dd-gold-light hover:to-dd-gold transition-colors disabled:opacity-60 tracking-wide flex items-center justify-center gap-2"
                  data-testid="admin-submit">
                  <Lock className="w-4 h-4" /> {loading ? "Signing in…" : "Access Admin Dashboard"}
                </button>
              </form>

              <div className="mt-5 p-3 bg-white/5 border border-dd-gold/20 rounded-sm text-xs text-dd-cream-muted flex items-start gap-2">
                <Shield className="w-4 h-4 text-dd-gold mt-0.5 shrink-0" strokeWidth={1.5} />
                <div>
                  <div className="font-semibold text-dd-gold mb-0.5">For Owner/Admin Use Only</div>
                  Admin accounts are not available for public signup. Contact the founders for access.
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, type, value, onChange, testid }) {
  return (
    <div>
      <label className="dd-label text-dd-cream-muted block mb-2">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required
        className="w-full bg-white/5 border border-dd-gold/30 text-dd-cream placeholder:text-dd-cream-muted/60 rounded-sm px-4 py-3 focus:border-dd-gold focus:ring-1 focus:ring-dd-gold outline-none"
        data-testid={testid}
      />
    </div>
  );
}
